import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Edit3,
  Info,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import { decisionLabel } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord, GateDecisionStatus, Project } from '@/types'
import { ArtifactViewer } from '../artifacts/ArtifactViewer'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'
import { AutoApproveAllButton } from '@/components/command-center/AutoApproveAllButton'

export interface ContentAwareG3Props {
  project: Project
  runId: number
  onApproved?: () => void
}

export function ContentAwareG3Gate({ project, runId, onApproved }: ContentAwareG3Props) {
  const gateNumber = 3
  const draftKey = gateDraftKey(project.id, runId, gateNumber)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, gateNumber],
    queryFn: () => api.getGate(project.id, runId, gateNumber),
    retry: false,
  })

  // Pull BRD / SRD / compliance-risks bodies so the per-decision context
  // block can show concrete counts instead of "go check the artifacts
  // below" boilerplate (plan §P0.6). Each fetch is best-effort — a 404
  // just leaves the count empty so the block still renders sensibly.
  const [brd, setBrd] = useState('')
  const [srd, setSrd] = useState('')
  const [compliance, setCompliance] = useState('')
  useEffect(() => {
    let cancelled = false
    const runStr = String(runId).padStart(3, '0')
    const grab = async (rel: string, set: (s: string) => void) => {
      try {
        const res = await fetch(projectFileUrl(project.id, rel))
        if (res.ok && !cancelled) set(await res.text())
      } catch {
        /* ignore — empty string is the empty state */
      }
    }
    grab(`runs/run-${runStr}/phase3/brd.md`, setBrd)
    grab(`runs/run-${runStr}/phase3/srd.md`, setSrd)
    grab(`runs/run-${runStr}/phase3/compliance-risks.md`, setCompliance)
    return () => { cancelled = true }
  }, [project.id, runId])

  const counts = useMemo(() => {
    // BR-### / SR-### are the primary id forms produced by the Generation
    // Swarm. Match a leading "##" so we don't double-count the same id
    // when it's referenced from a body paragraph.
    const brCount = (brd.match(/^##\s+BR-\d+/gm) ?? []).length
    const srCount = (srd.match(/^##\s+SR-\d+/gm) ?? []).length
    const regCount = (compliance.match(/^[-*]\s+/gm) ?? []).length
    return { brCount, srCount, regCount }
  }, [brd, srd, compliance])

  // Seed decisions from the freshly-fetched gate payload, reconciling
  // any persisted localStorage draft against the server's decision-id
  // set first. See ``reconcileDraftWithGate`` for the stale-draft
  // hygiene that keeps a deleted-and-recreated project from inheriting
  // the prior project's Approve/Edit/Reject picks.
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, gateNumber),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      gate.refetch()
      toast({ variant: 'success', title: 'G3 re-emitted' })
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Re-emit failed', description: (err as Error).message }),
  })

  const submit = useMutation({
    mutationFn: () => api.submitGateDecisions(project.id, runId, gateNumber, decisions),
    onSuccess: (g) => {
      gate.refetch()
      if (g.status === 'approved') {
        toast({ variant: 'success', title: 'G3 approved', description: 'Ready to run Phase 4.' })
      }
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Could not submit decisions', description: (err as Error).message }),
  })

  const allDecided = decisions.length > 0 && decisions.every((d) => d.status !== 'pending')
  const gateCleared = gate.data?.status === 'approved' || gate.data?.status === 'modified'

  const updateDecision = (id: string, patch: Partial<GateDecisionRecord>) =>
    setDecisions(decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)))

  const missing = gate.error instanceof ApiError && gate.error.status === 404
  const notVisible = gate.error instanceof ApiError && gate.error.code === 'gate.not_visible'

  // Per-decision "What you're approving" copy. Mirrors G2's DecisionContext
  // pattern so the architect can sanity-check from concrete counts instead
  // of a question-text-only sea (plan §P0.6 / audit §6.3.1).
  const contextFor = (d: GateDecisionRecord): ReactNode => {
    if (d.id === 'G3-D1') {
      return (
        <p>
          <strong className="text-slate-100">{counts.brCount || '—'}</strong> business
          requirement{counts.brCount === 1 ? '' : 's'} were extracted into{' '}
          <code className="rounded bg-slate-800/80 px-1 py-0.5 text-[11px] text-indigo-300">brd.md</code>.
          {' '}Spot-check that each one ties back to a goal in the project charter — gaps mean
          the BA missed something Phase 1 already captured.
        </p>
      )
    }
    if (d.id === 'G3-D2') {
      return (
        <p>
          <strong className="text-slate-100">{counts.srCount || '—'}</strong> system
          requirement{counts.srCount === 1 ? '' : 's'} live in{' '}
          <code className="rounded bg-slate-800/80 px-1 py-0.5 text-[11px] text-indigo-300">srd.md</code>.
          {' '}Each should follow EARS form (<em>The system shall…</em>) and trace back to
          a BR. The Critic will flag drift at G4, but catching it here saves a Phase-3 re-run.
        </p>
      )
    }
    if (d.id === 'G3-D3') {
      return (
        <p>
          {counts.regCount > 0 ? (
            <>
              Safety/Compliance flagged{' '}
              <strong className="text-slate-100">{counts.regCount}</strong> regulation
              {counts.regCount === 1 ? '' : 's'} in{' '}
              <code className="rounded bg-slate-800/80 px-1 py-0.5 text-[11px] text-indigo-300">compliance-risks.md</code>.
              {' '}Reject if you spot a missing regulation that applies to your domain — it
              has to land before Phase 4 so the Critic audits against the full set.
            </>
          ) : (
            <>
              No regulations were flagged. Approve if your domain genuinely has none, or{' '}
              Reject and Re-emit if Safety/Compliance was supposed to surface domain-specific
              regs (HIPAA, GDPR, PCI-DSS, etc.).
            </>
          )}
        </p>
      )
    }
    if (d.id === 'G3-D4') {
      return (
        <p>
          Diagrams and wireframes are listed in the artifact preview below. Confirm every
          requirement in the SRD traces to at least one diagram or screen — orphaned
          requirements are usually a sign the architecture or UX coverage is incomplete.
        </p>
      )
    }
    return null
  }

  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn"><AlertCircle className="h-3 w-3" /> Needs you</span>
            <span className="alm-chip">Gate G3</span>
            {decisions.length > 0 && <span className="alm-chip alm-chip-info">{decisions.length} decisions</span>}
            {gate.data?.status && <span className="alm-chip">{gate.data.status}</span>}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">Swarm Sanity Check</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            A thorough check to ensure the generated drafts are cohesive before running the Critic audit.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button type="button" onClick={() => emit.mutate()} disabled={emit.isPending} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50">
            {emit.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Re-emit
          </button>
        </div>
      </div>

      {notVisible && (
        <div className="rounded-md border border-amber-700/60 bg-amber-950/20 p-4 mb-5 text-sm text-amber-200/80">
          <p className="font-semibold">Gate skipped</p>
          <p className="mt-1">G3 is not visible at the current intervention budget.</p>
        </div>
      )}

      {missing && !notVisible && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-5 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">Click <strong>Re-emit</strong> above to build the G3 artifact.</p>
        </div>
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {!missing && !notVisible && (
        <>
          {/* Why-this-gate explainer (mirrors G1/G2 onboarding hygiene). */}
          <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mb-5 flex gap-2 text-xs text-slate-400">
            <Info className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-200">Why this gate exists:</strong> the Generation
              Swarm just produced your BRD, SRD, compliance risks, diagrams, and wireframes
              in one large fan-out. G3 is a thorough-cadence sanity pause — a 90-second
              cross-check before the Critic spends LLM tokens re-auditing every requirement at G4.
              Catching coherence problems here costs nothing; catching them at G4 costs a
              full Phase-3 rerun (~5–10 min and ~$0.90 in LLM costs).
            </div>
          </div>

          {/* Decisions */}
          <div className="space-y-3 mb-6">
            {decisions.map((decision) => (
              <div
                key={decision.id}
                className="alm-card"
                data-testid={`gate-${gateNumber}-row`}
              >
                 <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="text-[10.5px] text-slate-500">{decision.id}</div>
                    <div className="text-sm text-slate-100 mt-0.5">{decision.question}</div>
                  </div>
                  <div className="flex gap-1.5 shrink-0 flex-wrap">
                    {(['approved', 'edited', 'rejected'] as GateDecisionStatus[]).map((status) => {
                      const testSuffix = status === 'approved' ? 'approve' : status === 'edited' ? 'modify' : 'reject'
                      return (
                        <button
                          key={status}
                          type="button"
                          data-testid={`gate-${gateNumber}-${testSuffix}`}
                          onClick={() => updateDecision(decision.id, { status })}
                          className="alm-decision"
                          data-selected={decision.status === status ? status : undefined}
                        >
                          {status === 'approved' ? <Check className="h-3 w-3" /> : status === 'edited' ? <Edit3 className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          <span>{decisionLabel(status)}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
                {(() => {
                  const ctx = contextFor(decision)
                  return ctx ? (
                    <div className="rounded-md border border-slate-700/50 bg-slate-950/40 p-3 mt-2 mb-1">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                        What you're approving
                      </div>
                      <div className="text-[12.5px] leading-relaxed text-slate-300 [&>p]:m-0">{ctx}</div>
                    </div>
                  ) : null
                })()}
                {decision.status === 'edited' && (
                  <textarea
                    data-testid={`gate-${gateNumber}-edit-text`}
                    value={decision.answer ?? ''}
                    onChange={(e) => updateDecision(decision.id, { answer: e.target.value })}
                    rows={2}
                    placeholder="Describe your edits/corrections..."
                    className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mt-2"
                  />
                )}
                {decision.status === 'rejected' && (
                  <textarea
                    data-testid={`gate-${gateNumber}-rationale`}
                    value={decision.rejection_reason ?? ''}
                    onChange={(e) => updateDecision(decision.id, { rejection_reason: e.target.value })}
                    rows={2}
                    placeholder="Why are you rejecting this?"
                    className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mt-2"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mb-6 border border-slate-700/60 rounded-xl bg-slate-900/40 p-4">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-3">Generated Artifacts Preview</div>
            <div className="h-[400px] overflow-auto rounded border border-slate-700/50">
              <ArtifactViewer projectId={project.id} runId={runId} />
            </div>
          </div>

          {gate.data?.status === 'approved' && (
            <div className="rounded-lg p-4 mb-5 border border-emerald-700/40 bg-emerald-950/20 flex items-center gap-2 text-emerald-300 text-sm">
              <CheckCircle2 className="h-4 w-4" /> G3 approved — continue to the next step
            </div>
          )}

          {/* Primary actions. A gate advances to the NEXT STEP (which may
              be another gate, e.g. G3.5) — it never runs a phase. Phases
              are run from the Pipeline Runner once their turn comes. */}
          <div className="flex flex-wrap items-center gap-2">
            {gateCleared ? (
              <button type="button" onClick={() => onApproved?.()} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500">
                <CheckCircle2 className="h-4 w-4" /> Continue
              </button>
            ) : (
              <>
                <AutoApproveAllButton
                  decisions={decisions}
                  setDecisions={setDecisions}
                  gateName="G3"
                  disabled={submit.isPending}
                />
                <button type="button" data-testid={`gate-${gateNumber}-submit`} onClick={() => submit.mutate()} disabled={!allDecided || submit.isPending} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500">
                  {submit.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Check className="h-4 w-4" /> Approve & continue</>}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
